/**
 * Dexie-backed registry of saved stations. Used by RegistryPage and by
 * App.tsx's URL boot-intent resolution (e.g. ?CPID without ?WSURL needs to
 * look up matching saved stations).
 *
 * FEAT-004 (Session 8f.3): added `add` and `duplicate` so users can
 * manage the registry without going through a URL boot.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { StationRepositoryDexie } from '@/persistence/dexie/repositories';
import type { StationSnapshot } from '@/domain';
import {
  ConnectorId,
  EvseId,
  OcppVersion,
  type OcppVersion as OcppVersionT,
  type StationId,
} from '@/domain/types';
import { newStationId } from '@/domain/newStationId';

export interface RegistryStoreState {
  readonly entries: readonly StationSnapshot[];
  readonly status: 'idle' | 'loading' | 'ready';
}

export interface NewStationInput {
  readonly cpid: string;
  readonly wsUrl: string;
  readonly displayName?: string;
  readonly ocppVersion?: OcppVersionT;
}

export interface AddResult {
  readonly ok: boolean;
  readonly stationId?: StationId;
  readonly error?: string;
}

export interface UpdateStationInput {
  readonly cpid: string;
  readonly wsUrl: string;
  readonly displayName?: string;
  readonly ocppVersion?: OcppVersionT;
}

export interface RegistryStoreActions {
  refresh(): Promise<void>;
  remove(id: StationId): Promise<void>;
  findByCpid(cpid: string): Promise<readonly StationSnapshot[]>;
  findByIdentity(cpid: string, wsUrl: string): Promise<StationSnapshot | null>;
  /**
   * Add a fresh station with a minimum default 1×AC22 topology. Refuses
   * (and reports the error) if the (cpid, wsUrl) pair already exists.
   */
  add(input: NewStationInput): Promise<AddResult>;
  /**
   * Update cpid / wsUrl / displayName of an existing station.
   * Refuses if the new (cpid, wsUrl) pair clashes with a different station.
   */
  update(id: StationId, input: UpdateStationInput): Promise<AddResult>;
}

export type RegistryStore = RegistryStoreState & RegistryStoreActions;

const repo = new StationRepositoryDexie(db);

const DEFAULT_TOPOLOGY: StationSnapshot['topology'] = {
  cpMaxPowerW: 22_000,
  evses: [
    {
      id: EvseId.of(1),
      name: 'EVSE 1 (AC)',
      maxPowerW: 22_000,
      connectorIds: [ConnectorId.of(1)],
    },
  ],
};

export const useRegistryStore = create<RegistryStore>((set) => ({
  entries: [],
  status: 'idle',

  async refresh(): Promise<void> {
    set({ status: 'loading' });
    const entries = await repo.list();
    set({ entries, status: 'ready' });
  },

  async remove(id: StationId): Promise<void> {
    await repo.delete(id);
    const entries = await repo.list();
    set({ entries });
  },

  async findByCpid(cpid: string): Promise<readonly StationSnapshot[]> {
    const all = await repo.list();
    return all.filter((s) => s.cpid === cpid);
  },

  async findByIdentity(cpid: string, wsUrl: string): Promise<StationSnapshot | null> {
    return repo.findByIdentity(cpid, wsUrl);
  },

  async add(input): Promise<AddResult> {
    const cpid = input.cpid.trim();
    const wsUrl = input.wsUrl.trim();
    if (cpid.length === 0 || wsUrl.length === 0) {
      return { ok: false, error: 'cpid and wsUrl are required.' };
    }
    if (!/^[A-Za-z0-9_\-:.]+$/.test(cpid)) {
      return { ok: false, error: 'cpid must match [A-Za-z0-9_-:.]' };
    }
    const clash = await repo.findByIdentity(cpid, wsUrl);
    if (clash) {
      return {
        ok: false,
        error: `A station with cpid="${cpid}" and that wsUrl already exists.`,
      };
    }
    const id = newStationId();
    const snap: StationSnapshot = {
      id,
      cpid,
      wsUrl,
      ...(input.displayName !== undefined && input.displayName.length > 0
        ? { displayName: input.displayName }
        : {}),
      ocppVersion: input.ocppVersion ?? OcppVersion.V16J,
      topology: DEFAULT_TOPOLOGY,
      createdAt: Date.now(),
    };
    await repo.upsert(snap);
    const entries = await repo.list();
    set({ entries });
    return { ok: true, stationId: id };
  },

  async update(id: StationId, input: UpdateStationInput): Promise<AddResult> {
    const cpid = input.cpid.trim();
    const wsUrl = input.wsUrl.trim();
    if (cpid.length === 0 || wsUrl.length === 0) {
      return { ok: false, error: 'cpid and wsUrl are required.' };
    }
    if (!/^[A-Za-z0-9_\-:.]+$/.test(cpid)) {
      return { ok: false, error: 'cpid must match [A-Za-z0-9_-:.]' };
    }
    const all = await repo.list();
    const current = all.find((s) => s.id === id);
    if (!current) return { ok: false, error: 'Station not found.' };
    const clash = all.find((s) => s.id !== id && s.cpid === cpid && s.wsUrl === wsUrl);
    if (clash) {
      return { ok: false, error: `A station with cpid="${cpid}" and that wsUrl already exists.` };
    }
    const { displayName: _removed, ...rest } = current;
    const updated: StationSnapshot = {
      ...rest,
      cpid,
      wsUrl,
      ...(input.ocppVersion !== undefined ? { ocppVersion: input.ocppVersion } : {}),
      ...(input.displayName !== undefined && input.displayName.trim().length > 0
        ? { displayName: input.displayName.trim() }
        : {}),
    };
    await repo.upsert(updated);
    const entries = await repo.list();
    set({ entries });
    return { ok: true, stationId: id };
  },
}));
